import { useEffect, useMemo, useState } from 'react';
import { AgentSidebar } from '../CampaignLaunchWorkflow/agent/AgentSidebar';
import { BulkEditPanel } from '../CampaignLaunchWorkflow/agent/BulkEditPanel';
import { OptimizationActionBar } from '../CampaignLaunchWorkflow/agent/OptimizationActionBar';
import { OptimizationRunBar } from '../CampaignLaunchWorkflow/agent/OptimizationRunBar';
import { OptimizationRunGate } from '../CampaignLaunchWorkflow/agent/OptimizationRunGate';
import { isAgentWorking } from '../CampaignLaunchWorkflow/agent/agentBusy';
import { previewReadyToShow } from '../CampaignLaunchWorkflow/agent/previewGate';
import { useOptimizationSession, OPT_PHASE } from '../../hooks/useOptimizationSession';
import OptimizeCampaignModal from '../OptimizeCampaignModal';
import '../CampaignLaunchWorkflow/CampaignLaunchWorkflow.css';

const MIN_WIDTH = 360;
const MAX_WIDTH = 960;
const DEFAULT_WIDTH = 520;
const WIDTH_KEY = 'performance-sidekick-w';

/**
 * The Performance page's right rail.
 *
 * This is intentionally the same Optimization agent used by the campaign
 * surface opened from Campaign Grid: the same session hook, run gate, run
 * controls, confirmation card and live-platform apply path. Performance is an
 * account-wide page, though, while an optimization can only target one live
 * campaign. On an account view the selector below therefore establishes that
 * scope before the hook is enabled; a /performance/:campaignId drill-down is
 * already scoped and skips the choice.
 */
export default function PerformanceSidekick({
  open,
  setOpen,
  accountId,
  userId,
  campaignId = null,
  campaign = null,
  campaigns = [],
  accountSettings = {},
  inline = false,
}) {
  const [pickedId, setPickedId] = useState(campaignId || '');
  const [width, setWidth] = useState(() => {
    try {
      const saved = Number(window.localStorage.getItem(WIDTH_KEY));
      if (saved >= MIN_WIDTH && saved <= MAX_WIDTH) return saved;
    } catch (_) { /* private mode */ }
    return DEFAULT_WIDTH;
  });
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => { setPickedId(campaignId || ''); }, [accountId, campaignId]);

  const choices = useMemo(() => {
    const byId = new Map();
    if (campaign?.id) byId.set(campaign.id, campaign);
    for (const item of campaigns || []) {
      if (item?.id && !byId.has(item.id)) byId.set(item.id, item);
    }
    return [...byId.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [campaign, campaigns]);

  const selectedId = campaignId || pickedId || null;
  const selected = (campaign?.id === selectedId ? campaign : null)
    || choices.find((item) => item.id === selectedId)
    || null;

  const agent = useOptimizationSession({
    accountId,
    userId,
    campaignId: selectedId,
    destination: selected?.destination || null,
    campaignName: selected?.name || null,
    accountSettings,
    // Selecting a campaign is the explicit boundary. Merely loading
    // /performance never creates a session or starts a paid optimizer run.
    enabled: open && Boolean(selectedId),
  });

  const startResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = width;
    document.body.classList.add('clw-col-resizing');
    const onMove = (moveEvent) => {
      const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(
        startWidth + (startX - moveEvent.clientX),
      )));
      setWidth(next);
      try { window.localStorage.setItem(WIDTH_KEY, String(next)); } catch (_) { /* ignore */ }
    };
    const onUp = () => {
      document.body.classList.remove('clw-col-resizing');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const feedItems = [
    previewReadyToShow(agent) && {
      key: `opt-preview-${agent.activePreview.preview_id}`,
      pin: 'end',
      node: (
        <BulkEditPanel
          preview={agent.activePreview}
          onConfirm={(id) => agent.approvePreview(id, 'click')}
          onDismiss={(id) => agent.sendMessage(`I'm not applying preview ${id}. Don't apply it.`)}
          approving={agent.approving}
          approveError={agent.approveError}
        />
      ),
    },
    !previewReadyToShow(agent)
      && !agent.applying
      && agent.digest
      && agent.messages?.length > 0 && {
      key: 'opt-actions',
      pin: 'end',
      node: (
        <OptimizationActionBar
          digest={agent.digest}
          busy={isAgentWorking(agent)}
          onAsk={(text) => agent.sendMessage(text)}
        />
      ),
    },
    agent.applying && {
      key: 'opt-applying',
      pin: 'end',
      node: <div className="clw-bulk-result ok"><span>Sending the edits to the platform…</span></div>,
    },
    agent.applyReport && {
      key: `opt-applied-${agent.applyReport.ts}`,
      ts: agent.applyReport.ts,
      node: (() => {
        const results = agent.applyReport.results || [];
        const failed = results.filter((result) => !result.ok);
        return (
          <div className={`clw-bulk-result ${failed.length ? 'warn' : 'ok'}`}>
            <span>
              {`Dispatched ${results.length - failed.length} of ${results.length} edit run(s) to the platform.`}
              {' Each takes a minute or two.'}
              {failed.length > 0 && (
                <> Failed: {failed.map((item) => `${item.label || `#${item.index}`} — ${item.error}`).join('; ')}</>
              )}
            </span>
          </div>
        );
      })(),
    },
    agent.lastOptimizationFailure && !agent.lastOptimizationFailure.awaitingApproval && {
      key: `opt-failed-${agent.lastOptimizationFailure.ts}`,
      ts: agent.lastOptimizationFailure.ts,
      node: (
        <div className="clw-bulk-result warn">
          <span>{agent.lastOptimizationFailure.error}</span>
        </div>
      ),
    },
  ].filter(Boolean);

  const campaignScope = !campaignId ? (
    <label className="perf-sidekick-scope">
      <span>Campaign</span>
      <select
        aria-label="Sidekick campaign"
        value={pickedId}
        onChange={(event) => setPickedId(event.target.value)}
      >
        <option value="">Select a campaign…</option>
        {choices.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select>
    </label>
  ) : null;

  const runBar = selectedId && agent.phase !== OPT_PHASE.INELIGIBLE ? (
    <OptimizationRunBar
      destination={agent.context?.destination || selected?.destination || null}
      hasRun={Boolean(agent.run)}
      running={agent.phase === OPT_PHASE.RUNNING}
      disabled={agent.phase === OPT_PHASE.INELIGIBLE || agent.phase === OPT_PHASE.LOADING}
      disabledReason={agent.phase === OPT_PHASE.INELIGIBLE ? agent.context?.reason : null}
      onRun={agent.startRun}
    />
  ) : null;

  const fallback = !selectedId ? (
    <div className="clw-opt-gate">
      <strong>Choose a campaign to start.</strong>
      <p>The Optimization agent works on one live campaign at a time.</p>
    </div>
  ) : (
    <OptimizationRunGate
      phase={agent.phase}
      context={agent.context}
      runError={agent.runError}
      platformLabel={(agent.context?.platform || selected?.destination || '').toUpperCase()}
      onUploadRun={agent.context?.needsReportingMetrics ? () => setUploadOpen(true) : null}
    />
  );

  return (
    <div
      className={`perf-sidekick-wrap clw ${open ? 'open' : ''}${inline ? ' perf-sidekick-inline' : ''}`}
      style={{ '--ai-panel-w': `${width}px` }}
    >
      <AgentSidebar
        open={open}
        setOpen={setOpen}
        onResizeStart={inline ? undefined : startResize}
        session={agent.phase === OPT_PHASE.READY ? agent : null}
        feedItems={feedItems}
        scopeNote={(campaignScope || runBar) ? (
          <div className="perf-sidekick-controls">{campaignScope}{runBar}</div>
        ) : null}
        config={{
          title: 'Optimization',
          note: 'Sidekick agent',
          placeholder: agent.phase === OPT_PHASE.READY ? {
            title: 'What do you want to apply?',
            body: (
              <>I&apos;ve read this campaign&apos;s analysis reports. I can <strong>explain</strong> any
                suggestion and <strong>apply</strong> the ones you pick. I&apos;ll show you exactly
                what changes and ask first.</>
            ),
            sectionLabel: 'Try asking',
            prompts: [
              'Which suggestions would move the needle most?',
              'Apply the high-priority ones.',
              'Why does it want to change that bid?',
              'Which of these are just advice, not changes?',
            ],
          } : null,
          autoQaMessage: null,
          loadingMessage: 'Reading the campaign analysis reports — summarising the findings.',
          inputPlaceholder: 'Ask about a suggestion, or say which to apply…',
          inputPlaceholderNoSession: agent.phase === OPT_PHASE.RUNNING
            ? 'Waiting for campaign analysis…'
            : 'Choose a campaign or run campaign analysis to get started',
          fallbackMessage: fallback,
          suggestionHint: null,
        }}
        context={{
          activeGroup: selected?.name || 'campaign',
          rowsCount: selectedId ? 1 : 0,
          rowLabel: selectedId ? 'campaign' : 'campaigns',
        }}
      />

      {uploadOpen ? (
        /* MadHive is the only optimizer that needs a reporting ZIP. This is the
           same upload modal and completion path used by the Campaign Grid's
           Optimization agent. */
        <OptimizeCampaignModal
          isOpen
          onClose={() => setUploadOpen(false)}
          campaign={{ ...selected, destination: agent.context?.destination || selected?.destination }}
          platformCampaignId={agent.context?.platformCampaignId || null}
          accountId={accountId}
          user={{ userId }}
          onOptimizeComplete={({ customPrompt, dateParams, reportingMetricsPath }) => (
            agent.startRun(customPrompt, dateParams, reportingMetricsPath)
          )}
        />
      ) : null}
    </div>
  );
}
